import React, { useEffect, useMemo, useState } from "react";
import { api, money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { generateInvoicePDF } from "@/utils/pdf";
import { Search, Download, ReceiptText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const num = (v) => Number(v || 0);
const paymentLabel = (status, orderStatus) => {
  if (orderStatus === "Cancelada" || status === "VOID") return "Sin efecto";
  if (status === "Cancelled") return "Pagada";
  return status || "—";
};

function legacyBillItems(order) {
  let source = [];

  if (Array.isArray(order.legacy_items)) {
    source = order.legacy_items;
  } else if (typeof order.legacy_items_json === "string" && order.legacy_items_json.trim()) {
    try {
      const parsed = JSON.parse(order.legacy_items_json);
      source = Array.isArray(parsed) ? parsed : [];
    } catch {
      source = [];
    }
  }

  return source.map((it, index) => {
    const fallbackName = [it.genero, it.cultivar, it.size]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(" · ");
    const price = num(it.price ?? it.unit_price);
    const quantity = Math.max(num(it.quantity ?? it.qty), 1);
    const lineTotal = it.line_total !== "" && it.line_total != null
      ? num(it.line_total)
      : price * quantity;

    return {
      item_id: it.item_id || `legacy-${order.order_id}-${index + 1}`,
      name: String(it.name || fallbackName || "Producto histórico"),
      price,
      quantity,
      line_total: lineTotal,
      historical_legacy: true,
    };
  });
}

function buildBill(order, orderItems) {
  const modernItems = orderItems
    .filter((it) => String(it.order_id) === String(order.order_id))
    .map((it) => ({
      item_id: it.product_id || it.order_item_id,
      name: it.product_name || it.product_id || "Producto",
      price: num(it.unit_price),
      quantity: num(it.qty),
      line_total: num(it.line_total),
    }));

  const legacyItems = modernItems.length === 0 ? legacyBillItems(order) : [];
  const items = modernItems.length ? modernItems : legacyItems;
  const hasLegacyMetadata = Boolean(
    order.legacy_history_source ||
    (typeof order.legacy_items_json === "string" && order.legacy_items_json.trim()) ||
    Array.isArray(order.legacy_items)
  );
  const isLegacy = modernItems.length === 0 && hasLegacyMetadata;
  const recordedTotal = order.total_amount !== "" && order.total_amount != null
    ? num(order.total_amount)
    : null;
  const itemSubtotal = items.reduce((sum, it) => sum + num(it.line_total), 0);
  const subtotal = order.subtotal_amount !== "" && order.subtotal_amount != null
    ? num(order.subtotal_amount)
    : (isLegacy && recordedTotal != null ? recordedTotal : itemSubtotal);
  const discount = num(order.discount_amount);
  const iva = num(order.iva_amount);
  const total = recordedTotal != null
    ? recordedTotal
    : Math.max(subtotal - discount, 0) + num(order.shipping_amount) + iva;

  return {
    id: String(order.order_id),
    bill_number: `#${order.order_id}`,
    order_id: String(order.order_id),
    customer_name: order.order_customer_name || "",
    customer_phone: order.customer_phone || "",
    created_at: order.order_date,
    items,
    subtotal,
    discount_percent: 0,
    discount_amount: discount,
    tax_percent: num(order.iva_rate),
    tax_amount: iva,
    shipping_amount: num(order.shipping_amount),
    total,
    payment_status: order.payment_status || "",
    order_status: order.order_status || "",
    payment_method: order.payment_method || "",
    delivery_type: order.delivery_type || "",
    historical_legacy: isLegacy,
    historical_note: order.legacy_history_note || "",
  };
}

export default function HistoryPage() {
  const [orders, setOrders] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/gsheet/orders", { params: { history_schema: "legacy-v1" } }),
      api.get("/gsheet/orderItems", { params: { history_schema: "legacy-v1" } }),
    ])
      .then(([ordersRes, itemsRes]) => {
        setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
        setOrderItems(Array.isArray(itemsRes.data) ? itemsRes.data : []);
      })
      .catch((err) => {
        const detail = err.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "No se pudo cargar Bill History.");
      })
      .finally(() => setLoading(false));
  }, []);

  const bills = useMemo(() => {
    return orders
      .filter((o) => String(o.order_id || "").trim())
      .map((o) => buildBill(o, orderItems))
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [orders, orderItems]);

  const normalizedSearch = String(search || "").trim().toLowerCase();
  const filtered = normalizedSearch
    ? bills.filter((b) => {
        const haystack = [b.bill_number, b.customer_name, b.customer_phone]
          .map((value) => String(value ?? "").toLowerCase())
          .join(" ");
        return haystack.includes(normalizedSearch);
      })
    : bills;

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-foreground">Bill History</h1>
          <p className="text-sm text-muted-foreground">{bills.length} bills generated from Orders_MASTER</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="bill-history-search-input"
            placeholder="Search bill # or customer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 w-64"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading bills...
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              <ReceiptText className="h-10 w-10 mx-auto mb-3 opacity-40" />
              No bills yet.
            </div>
          )}
          {filtered.map((b) => (
            <div
              key={b.id}
              data-testid={`bill-history-item-${b.id}`}
              className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-card p-4 rounded-xl border border-border hover:border-accent transition-colors gap-3"
            >
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono font-semibold text-foreground">{b.bill_number}</span>
                  <span className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full">
                    {b.items.length} items
                  </span>
                  {b.historical_legacy && (
                    <span className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full">
                      Histórico
                    </span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${b.order_status === "Cancelada" ? "bg-destructive/10 text-destructive" : "bg-emerald-500/10 text-emerald-700"}`}>
                    {b.order_status || "—"}
                  </span>
                  <span className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full">
                    {paymentLabel(b.payment_status, b.order_status)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {b.customer_name || "Walk-in"} {b.customer_phone && `· ${b.customer_phone}`} ·{" "}
                  {b.created_at ? new Date(b.created_at).toLocaleString("es-CR") : "—"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono font-bold text-lg text-primary">{money(b.total)}</span>
                <Button variant="outline" size="sm" onClick={() => setSelected(b)} data-testid={`view-bill-detail-button-${b.id}`}>
                  View
                </Button>
                <Button size="sm" className="gap-1.5" onClick={() => generateInvoicePDF(b)} data-testid={`download-bill-pdf-button-${b.id}`}>
                  <Download className="h-4 w-4" /> PDF
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="bill-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-display">Bill {selected?.bill_number}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                {selected.customer_name || "Walk-in"} {selected.customer_phone && `· ${selected.customer_phone}`}
              </p>
              <div className="flex gap-2 text-xs flex-wrap">
                <span className="rounded-full bg-secondary px-2 py-1">Orden: {selected.order_status || "—"}</span>
                <span className="rounded-full bg-secondary px-2 py-1">Pago: {paymentLabel(selected.payment_status, selected.order_status)}</span>
                {selected.historical_legacy && (
                  <span className="rounded-full bg-secondary px-2 py-1">Fuente: histórico legacy</span>
                )}
              </div>
              {selected.historical_legacy && selected.historical_note && (
                <p className="rounded-lg bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
                  {selected.historical_note}
                </p>
              )}
              <div className="border-y border-border divide-y divide-border">
                {selected.items.map((it, i) => (
                  <div key={i} className="flex justify-between py-2 gap-3">
                    <span>{it.name} <span className="text-muted-foreground">× {it.quantity}</span></span>
                    <span className="font-mono whitespace-nowrap">{money(it.line_total ?? it.price * it.quantity)}</span>
                  </div>
                ))}
                {selected.items.length === 0 && selected.historical_legacy && (
                  <div className="py-3 text-xs text-muted-foreground">
                    No se localizó detalle de líneas con evidencia suficiente para esta orden histórica.
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <Line label={selected.historical_legacy ? "Total histórico registrado" : "Subtotal"} value={money(selected.subtotal)} />
                {selected.discount_amount > 0 && <Line label="Descuento" value={"- " + money(selected.discount_amount)} />}
                {selected.shipping_amount > 0 && <Line label="Envío" value={money(selected.shipping_amount)} />}
                {selected.tax_amount > 0 && <Line label={`IVA (${selected.tax_percent}%)`} value={money(selected.tax_amount)} />}
                <div className="flex justify-between font-bold text-base pt-1 border-t border-border">
                  <span>Total</span>
                  <span className="font-mono text-primary">{money(selected.total)}</span>
                </div>
              </div>
              <Button className="w-full gap-2" onClick={() => generateInvoicePDF(selected)}>
                <Download className="h-4 w-4" /> Download PDF
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Line = ({ label, value }) => (
  <div className="flex justify-between text-muted-foreground">
    <span>{label}</span>
    <span className="font-mono">{value}</span>
  </div>
);
